/**
 * Scheduled commands — the only way anything outside the simulation changes it.
 *
 * The world runs the same deterministic simulation on the server and in every
 * client (ADR-002 D6). Two copies of a deterministic simulation stay identical
 * only if they see the same inputs *at the same tick*, so a player action is
 * not applied when it arrives. It is stamped with a tick in the near future,
 * broadcast, and applied at the head of that tick by everyone at once.
 *
 * This is deterministic lockstep with input delay — the model RTS games have
 * used since the 1990s, chosen here for the same reason they chose it: the
 * state is far too large to ship over the wire, and far too large to rewind
 * casually, but the *inputs* are tiny.
 *
 * The cadence makes it comfortable. At the canonical 6 Hz a tick is 167 ms, so
 * a two-tick delay absorbs a third of a second of round-trip latency before
 * anything has to be reconsidered. A mayor's verbs are things like "commit this
 * road" — a third of a second of scheduling delay on a road that takes a season
 * to matter is not a perceptible lag, and it buys a netcode with no rollback in
 * the common case.
 *
 * This module is part of the deterministic core: it decides *order*, never
 * *timing*. The policy that picks which future tick to aim at is a client
 * concern and lives in the client's `InputDelay.ts`, where it cannot touch sim
 * state.
 *
 * The envelope and the total order are not here. They are in
 * `@antification/protocol`, because they are what a client, a district and a
 * server have to agree on; this buffer is one way of holding the commands, and a
 * server will hold them another way.
 */

import {
  Command,
  CommandQueueState,
  ScheduleResult,
  compareCommands,
} from '@antification/protocol';

/**
 * The pending-command buffer, held identically by server and clients.
 *
 * Its contents are canonical state: a command accepted for tick 4,100 but not
 * yet applied is part of what the world is, and a server that restarts without
 * it settles a different season.
 */
export class CommandQueue {
  private byTick = new Map<number, Command[]>();

  /** Highest tick already applied. Anything at or below it is late by definition. */
  private lastAppliedTick = -1;

  /** Guards against a retransmission being applied twice. */
  private seen = new Set<string>();

  private static key(c: Command): string {
    return `${c.actor}#${c.seq}`;
  }

  /**
   * Accept a command for its stamped tick.
   *
   * Rejection is not an error path to be papered over — it is the signal that
   * the issuer's estimate of the network was wrong, and the caller has to
   * decide what that means. A server rescheduling to `earliestTick` keeps the
   * world moving at the cost of a client rollback; a server refusing keeps
   * every client's prediction correct at the cost of losing the input. Which
   * one is right depends on the verb, so this class does not choose.
   */
  schedule(command: Command): ScheduleResult {
    const key = CommandQueue.key(command);
    const existing = this.find(key);
    if (existing) return { ok: false, reason: 'duplicate', existing };

    if (command.tick <= this.lastAppliedTick) {
      return { ok: false, reason: 'late', earliestTick: this.lastAppliedTick + 1 };
    }

    const bucket = this.byTick.get(command.tick);
    if (bucket) bucket.push(command);
    else this.byTick.set(command.tick, [command]);
    this.seen.add(key);

    return { ok: true, command };
  }

  /**
   * Take everything due at `tick`, in canonical order.
   *
   * Call at the head of the tick, before the simulation advances, on every peer.
   * Draining is monotonic: a tick is drained once and never revisited, because
   * revisiting one would mean the two peers disagree about what has happened.
   */
  drain(tick: number): Command[] {
    if (tick <= this.lastAppliedTick) {
      throw new RangeError(
        `tick ${tick} was already applied (last was ${this.lastAppliedTick}); ` +
        `re-applying a tick means the peers disagree about history`
      );
    }
    this.lastAppliedTick = tick;

    const due = this.byTick.get(tick);
    if (!due) return [];
    this.byTick.delete(tick);
    for (const c of due) this.seen.delete(CommandQueue.key(c));

    // Sorted on the way out rather than on the way in: arrival order is the
    // thing being neutralised, so the sort must be the last word.
    return due.sort(compareCommands);
  }

  /** Commands accepted but not yet applied, in canonical order. */
  pending(): Command[] {
    const all: Command[] = [];
    for (const bucket of this.byTick.values()) all.push(...bucket);
    return all.sort(compareCommands);
  }

  /** The earliest tick a new command may be stamped with. */
  earliestSchedulableTick(): number {
    return this.lastAppliedTick + 1;
  }

  /** Highest tick already applied, or -1 before the world starts. */
  get appliedThrough(): number {
    return this.lastAppliedTick;
  }

  private find(key: string): Command | undefined {
    if (!this.seen.has(key)) return undefined;
    for (const bucket of this.byTick.values()) {
      for (const c of bucket) if (CommandQueue.key(c) === key) return c;
    }
    return undefined;
  }

  save(): CommandQueueState {
    return { lastAppliedTick: this.lastAppliedTick, pending: this.pending() };
  }

  load(state: CommandQueueState): void {
    this.byTick.clear();
    this.seen.clear();
    this.lastAppliedTick = state.lastAppliedTick;
    for (const c of state.pending) {
      const bucket = this.byTick.get(c.tick);
      if (bucket) bucket.push(c);
      else this.byTick.set(c.tick, [c]);
      this.seen.add(CommandQueue.key(c));
    }
  }
}
